import { request } from '@/renderer/utils/api';
import { IS_SERVER } from '@/renderer/utils/ssr';

export interface SeasonRow {
  seasonId: number;
  name: string;
  startDate: string;
  endDate: string;
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  betstampVerified: boolean;
}

export interface SportRecord {
  sportId: number;
  name: string;
  shortName: string;
  seasons: SeasonRow[];
  allTime: { wins: number; losses: number; pushes: number; units: number };
  betstamp: { wins: number; losses: number; pushes: number; units: number };
}

export interface DataType {
  sports?: SportRecord[];
}

// Sports to feature on the page, in display order
const FEATURED_SPORTS: { id: number; name: string; shortName: string }[] = [
  { id: 3, name: 'NFL', shortName: 'NFL' },
  { id: 1, name: 'College Football', shortName: 'CFB' },
  { id: 2, name: 'College Basketball', shortName: 'CBB' },
  { id: 4, name: 'WNBA', shortName: 'WNBA' },
];

// Betstamp verification start date
const BETSTAMP_START = new Date('2020-01-01T00:00:00Z');

const abortInit = () =>
  typeof AbortSignal === 'undefined'
    ? {}
    : { signal: AbortSignal.timeout(IS_SERVER ? 2000 : 10000) };

export async function data(): Promise<DataType> {
  try {
    const statsRequest = await request<API.ReleaseStats>(
      { input: '/api/v1/release/stats', init: abortInit() },
      {
        skipRefresh: true,
        cache: { maxAge: 10 * 60 * 1000 },
        query: {
          page: { limit: 200 },
          data: { playType: ['side', 'total', 'prop', 'future', 'not-available'] },
        },
      },
    );

    if (!statsRequest?.seasonId) {
      return {};
    }

    // Group season stats by sportId
    const bySport = new Map<
      number,
      {
        rows: SeasonRow[];
        allWins: number;
        allLosses: number;
        allPushes: number;
        allUnits: number;
        bsWins: number;
        bsLosses: number;
        bsPushes: number;
        bsUnits: number;
      }
    >();

    for (const featured of FEATURED_SPORTS) {
      bySport.set(featured.id, {
        rows: [],
        allWins: 0,
        allLosses: 0,
        allPushes: 0,
        allUnits: 0,
        bsWins: 0,
        bsLosses: 0,
        bsPushes: 0,
        bsUnits: 0,
      });
    }

    for (const [seasonIdKey, entry] of Object.entries(statsRequest.seasonId)) {
      const seasonMeta = entry.meta?.season?.[seasonIdKey as API.Id];
      if (!seasonMeta) {
        continue;
      }

      const sportId = Number(seasonMeta.sportId);
      if (!bySport.has(sportId)) {
        continue;
      }

      const wins = entry.stats?.['win']?.total ?? 0;
      const losses = entry.stats?.['loss']?.total ?? 0;
      const pushes = entry.stats?.['push']?.total ?? 0;
      const units = entry.units ?? 0;

      // Only include seasons with at least one settled result
      if (wins + losses + pushes === 0) {
        continue;
      }

      const endDate = seasonMeta.endDate ?? '';
      const startDate = seasonMeta.startDate ?? '';
      const betstampVerified = endDate ? new Date(endDate) >= BETSTAMP_START : false;

      const row: SeasonRow = {
        seasonId: Number(seasonIdKey),
        name: seasonMeta.name ?? String(seasonIdKey),
        startDate,
        endDate,
        wins,
        losses,
        pushes,
        units,
        betstampVerified,
      };

      const sport = bySport.get(sportId)!;
      sport.rows.push(row);
      sport.allWins += wins;
      sport.allLosses += losses;
      sport.allPushes += pushes;
      sport.allUnits += units;

      if (betstampVerified) {
        sport.bsWins += wins;
        sport.bsLosses += losses;
        sport.bsPushes += pushes;
        sport.bsUnits += units;
      }
    }

    const sports: SportRecord[] = FEATURED_SPORTS.flatMap((featured) => {
      const sport = bySport.get(featured.id);
      if (!sport || sport.rows.length === 0) {
        return [];
      }

      // Sort newest first
      const seasons = sport.rows.sort(
        (a, b) =>
          (b.endDate ? new Date(b.endDate).getTime() : 0) -
          (a.endDate ? new Date(a.endDate).getTime() : 0),
      );

      return [
        {
          sportId: featured.id,
          name: featured.name,
          shortName: featured.shortName,
          seasons,
          allTime: {
            wins: sport.allWins,
            losses: sport.allLosses,
            pushes: sport.allPushes,
            units: sport.allUnits,
          },
          betstamp: {
            wins: sport.bsWins,
            losses: sport.bsLosses,
            pushes: sport.bsPushes,
            units: sport.bsUnits,
          },
        },
      ];
    });

    return { sports };
  } catch (err) {
    console.error('verified-record dataFetcher error:', err);
    return {};
  }
}
